/**
 * Process flow — what waits for what (RV11, spec/19).
 *
 * Before this the record could not state an order at all, and the editor had nothing to show:
 * `operation_number` was a print rank that gated nothing, so "these four run at the same time"
 * and "these four run one after another" looked identical on every screen.
 *
 * WHY LEVELS AND NOT A CANVAS. The thing an author needs to see is which operations have no
 * order between them, and that is exactly what a topological level shows: everything in one row
 * may run at once. A drag-and-drop canvas would add positions the record does not have and
 * cannot store, and an author would then arrange boxes that mean nothing. Levels are computed
 * here because they are LAYOUT; every fact in them — the edges, the starts, the findings — comes
 * from the server.
 *
 * Parallelism is authored by SUBTRACTION: the record is chained first, and the author unorders
 * the pairs that are genuinely concurrent. That is why the only destructive act here asks for a
 * reason and an authority — the source documents state a chain, so unordering asserts something
 * the document does not say.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Modal, Select, Spin, Tag, Tooltip, message } from 'antd';

import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import { addOrderEdge, getRecordGraph, unorderEdge } from '@/utils/record_order_api';

const { TextArea } = Input;

/**
 * Longest-path levels. Longest, not shortest, so an operation sits below EVERY predecessor:
 * with shortest-path a step could be drawn beside something it actually waits for, which is the
 * one thing this view exists to make impossible to misread.
 */
const levelsOf = (operations, edges) => {
  const preds = new Map(operations.map((o) => [o.operation_id, []]));
  edges.forEach((e) => preds.get(e.to_operation_id)?.push(e.from_operation_id));

  const level = new Map();
  const depth = (id, seen = new Set()) => {
    if (level.has(id)) return level.get(id);
    if (seen.has(id)) return 0; // the write path refuses cycles; never loop if one arrives
    seen.add(id);
    const parents = preds.get(id) || [];
    const d = parents.length ? Math.max(...parents.map((p) => depth(p, seen))) + 1 : 0;
    level.set(id, d);
    return d;
  };
  operations.forEach((o) => depth(o.operation_id));

  const rows = [];
  operations.forEach((o) => {
    const d = level.get(o.operation_id) ?? 0;
    (rows[d] = rows[d] || []).push(o);
  });
  return rows.map((r) => r.sort((a, b) => a.operation_number - b.operation_number));
};

const SEVERITY_TONE = { blocking: 'error', advisory: 'warning' };

export default function RecordOrderPanel({ recipeId }) {
  // Read the theme rather than take it as a prop: a caller that forgets would render a white
  // card on the dark ground, and this panel is reached from two different hosts.
  const { isDark } = useTheme();
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [addTo, setAddTo] = useState(null);
  const [unordering, setUnordering] = useState(null);
  const [reason, setReason] = useState('');
  const [authority, setAuthority] = useState('');
  const [busy, setBusy] = useState(false);

  const muted = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const border = isDark ? defaultColors.palette.gray[700] : '#e5e7eb';
  const surface = isDark ? 'rgba(148,163,184,0.06)' : '#fff';

  const load = useCallback(
    async (signal) => {
      if (!recipeId) return;
      setLoading(true);
      setError('');
      try {
        setGraph(await getRecordGraph(recipeId, signal));
      } catch (e) {
        if (e?.name === 'CanceledError') return;
        // No fallback and no invented graph: an outage reads as an outage.
        setError(e?.message || 'The record order could not be read.');
      } finally {
        setLoading(false);
      }
    },
    [recipeId],
  );

  useEffect(() => {
    const c = new AbortController();
    load(c.signal);
    return () => c.abort();
  }, [load]);

  const operations = useMemo(() => graph?.operations || [], [graph]);
  const edges = useMemo(() => graph?.edges || [], [graph]);
  const rows = useMemo(() => levelsOf(operations, edges), [operations, edges]);
  const edgesInto = useMemo(() => {
    const m = new Map();
    edges.forEach((e) => {
      if (!m.has(e.to_operation_id)) m.set(e.to_operation_id, []);
      m.get(e.to_operation_id).push(e);
    });
    return m;
  }, [edges]);

  const refuse = (e) => {
    // The server's own words. A cycle names its ring; a subject mismatch names both operations'
    // loop subjects. Re-wording either would lose the part the author needs.
    const ring = e?.detail?.ring;
    message.error({
      content: ring?.length ? `${e.message} (${ring.join(' → ')})` : e.message,
      duration: 6,
    });
  };

  const doAdd = async (fromId) => {
    setBusy(true);
    try {
      await addOrderEdge(recipeId, { from_operation_id: fromId, to_operation_id: addTo.operation_id });
      setAddTo(null);
      await load();
      message.success('Order added');
    } catch (e) {
      refuse(e);
    } finally {
      setBusy(false);
    }
  };

  const doUnorder = async () => {
    setBusy(true);
    try {
      await unorderEdge(recipeId, unordering.id, { reason, authority });
      setUnordering(null);
      setReason('');
      setAuthority('');
      await load();
      message.success('Unordered — the record keeps the reason and who gave it');
    } catch (e) {
      refuse(e);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !graph) return <div className="py-8 text-center"><Spin /></div>;
  if (error) return <Alert type="error" showIcon message="Could not read the order" description={error} />;
  if (!operations.length) return <Empty description="This record has no operations yet." />;

  return (
    <div>
      {(graph.findings || []).map((f) => (
        <Alert
          key={f.code}
          className="mb-2"
          type={SEVERITY_TONE[f.severity] || 'warning'}
          showIcon
          message={f.label}
          description={
            <span style={{ fontSize: 12.5 }}>
              {f.why} <b>{f.fix}</b>
            </span>
          }
        />
      ))}

      <div style={{ fontSize: 12, color: muted, marginBottom: 10 }}>
        Operations on the same row have no order between them and may run at once. Adding an order
        is free; removing one asks for a reason and an authority, because the record then asserts
        something its source document does not.
      </div>

      {rows.map((row, i) => (
        <div key={i} className="flex items-stretch gap-2 mb-2 flex-wrap">
          <div style={{ width: 34, flex: 'none', color: muted, fontSize: 11, paddingTop: 10 }}>
            {i === 0 ? 'start' : `↓ ${i}`}
          </div>
          {row.map((op) => {
            const into = edgesInto.get(op.operation_id) || [];
            const isSel = selected === op.operation_id;
            return (
              <div
                key={op.operation_id}
                className="rounded-md px-3 py-2"
                style={{
                  border: `1px solid ${isSel ? defaultColors.primary : border}`,
                  backgroundColor: surface,
                  minWidth: 210,
                  flex: '1 1 210px',
                }}
              >
                <button
                  className="w-full text-left"
                  onClick={() => setSelected(isSel ? null : op.operation_id)}
                  style={{ fontSize: 13, fontWeight: 500 }}
                >
                  {op.operation_number}. {op.operation_name}
                </button>
                <div style={{ fontSize: 11, color: muted }}>{op.process_name}</div>

                {isSel && (
                  <div className="mt-2" style={{ borderTop: `1px solid ${border}`, paddingTop: 6 }}>
                    {into.length === 0 ? (
                      <div style={{ fontSize: 11.5, color: muted }}>Starts the record.</div>
                    ) : (
                      into.map((e) => (
                        <div key={e.id} className="flex items-center justify-between gap-2 mb-1">
                          <span style={{ fontSize: 11.5 }}>
                            after <b>{e.from}</b>
                            {e.scope === 'SUBJECT' && (
                              <Tooltip title="Ordered per subject — the operations overlap in time">
                                <Tag className="ml-1" color="gold" style={{ fontSize: 10 }}>
                                  per {e.subject}
                                </Tag>
                              </Tooltip>
                            )}
                          </span>
                          <Button size="small" danger type="text" onClick={() => setUnordering(e)}>
                            Unorder
                          </Button>
                        </div>
                      ))
                    )}
                    <Button size="small" className="mt-1" onClick={() => setAddTo(op)}>
                      Order after…
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Order after — every other operation of the record is offered; the server refuses a
          cycle, a self-edge or a cross-record pair and says which, so the list is not filtered
          on a guess about what would be valid. */}
      <Modal
        open={Boolean(addTo)}
        title={addTo ? `What must finish before "${addTo.operation_name}"?` : ''}
        onCancel={() => setAddTo(null)}
        footer={null}
        destroyOnHidden
      >
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Choose the operation it waits for"
          optionFilterProp="label"
          disabled={busy}
          onChange={doAdd}
          options={operations
            .filter((o) => o.operation_id !== addTo?.operation_id)
            .map((o) => ({ value: o.operation_id, label: `${o.operation_number}. ${o.operation_name}` }))}
        />
      </Modal>

      {/* Unorder — a reason AND an authority. The server enforces both; asking here means the
          author is not refused after the fact for something they were never asked for. */}
      <Modal
        open={Boolean(unordering)}
        title="Unorder these two operations"
        okText="Unorder"
        okButtonProps={{ danger: true, disabled: !reason.trim() || !authority.trim(), loading: busy }}
        onOk={doUnorder}
        onCancel={() => setUnordering(null)}
        destroyOnHidden
      >
        <p style={{ fontSize: 12.5, color: muted }}>
          {unordering ? `Operation ${unordering.from} will no longer have to finish before ${unordering.to}.` : ''}
          {' '}The row is kept, not deleted, so the record can always say who unordered them and why.
        </p>
        <TextArea
          rows={2}
          className="mb-2"
          placeholder="Why do these run at the same time?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <Input
          placeholder="Authority — a BMR/SOP clause, or the person taking it as plant practice"
          value={authority}
          onChange={(e) => setAuthority(e.target.value)}
        />
      </Modal>
    </div>
  );
}
